import { useEffect, useMemo, useRef, useState } from 'react'
import type { SVGProps } from 'react'
import { mountBunnyPlayer } from './bunny-player'
import type {
  CourseificChapter,
  CourseificCourse,
  CourseificLesson,
  CourseificVideo
} from './types'

/**
 * Controlled course player.
 *
 * The host app owns active lesson state, completion state, persistence, routing,
 * auth, and analytics. The player owns only local UI state such as expanded
 * chapters.
 */
export function CourseificPlayer({
  course,
  activeLessonId,
  completedLessonIds = [],
  completed = false,
  courseCompletionFailed = false,
  onLessonSelect,
  onLessonCompleted,
  onCourseCompletionRetry
}: {
  course: CourseificCourse
  activeLessonId?: string
  completedLessonIds?: string[]
  completed?: boolean
  courseCompletionFailed?: boolean
  onLessonSelect?: (lesson: CourseificLesson) => void
  onLessonCompleted?: (lesson: CourseificLesson) => void
  onCourseCompletionRetry?: () => void
}) {
  const lessons = useMemo(() => course.chapters.flatMap((chapter) => chapter.lessons), [course])
  const activeLesson = lessons.find((lesson) => lesson.id == activeLessonId) ?? lessons[0]
  const [expandedChapterIds, setExpandedChapterIds] = useState(() => {
    const activeChapter = course.chapters.find((chapter) =>
      chapter.lessons.some((lesson) => lesson.id == activeLesson?.id)
    )
    return new Set(activeChapter ? [activeChapter.id] : [course.chapters[0]?.id].filter(Boolean))
  })
  const completedSet = useMemo(() => new Set(completedLessonIds), [completedLessonIds])
  const activeLessonIndex = activeLesson
    ? lessons.findIndex((lesson) => lesson.id == activeLesson.id)
    : -1
  const previousLesson = activeLessonIndex > 0 ? lessons[activeLessonIndex - 1] : undefined
  const nextLesson =
    activeLessonIndex >= 0 && activeLessonIndex < lessons.length - 1
      ? lessons[activeLessonIndex + 1]
      : undefined
  const completionPercent = lessons.length
    ? Math.round((completedSet.size / lessons.length) * 100)
    : 0

  useEffect(() => {
    const activeChapter = course.chapters.find((chapter) =>
      chapter.lessons.some((lesson) => lesson.id == activeLesson?.id)
    )
    if (!activeChapter) return

    setExpandedChapterIds((current) => {
      if (current.has(activeChapter.id)) return current

      const next = new Set(current)
      next.add(activeChapter.id)
      return next
    })
  }, [activeLesson?.id, course.chapters])

  function selectLesson(lesson: CourseificLesson) {
    onLessonSelect?.(lesson)
  }

  function completeLesson(lesson: CourseificLesson) {
    if (!completedSet.has(lesson.id)) {
      onLessonCompleted?.(lesson)
    }
  }

  function toggleChapter(chapterId: string) {
    setExpandedChapterIds((current) => {
      const next = new Set(current)
      if (next.has(chapterId)) {
        next.delete(chapterId)
      } else {
        next.add(chapterId)
      }
      return next
    })
  }

  return (
    <div className="grid min-h-screen grid-cols-1 bg-gray-900 font-sans text-white lg:grid-cols-3">
      <aside
        className="overflow-y-auto border-r border-black bg-gray-800 lg:col-span-1"
        aria-label="Chapters"
      >
        <div className="border-b border-gray-700 px-4 py-5">
          <h1 className="m-0 text-3xl font-bold leading-snug">{course.title}</h1>
          {completed ? (
            <div className="mt-5 rounded-md bg-green-300 px-4 py-2 text-center text-base font-bold text-green-800">
              Course completed
            </div>
          ) : null}
          {courseCompletionFailed ? (
            <div className="mt-5 rounded-md bg-red-100 px-4 py-3 text-center text-sm font-semibold text-red-800">
              Course completion wasn't saved.{' '}
              <button className="underline" type="button" onClick={onCourseCompletionRetry}>
                Retry
              </button>
            </div>
          ) : null}
          <div className="mt-5 h-1 overflow-hidden rounded-full bg-gray-600">
            <div className="h-full bg-blue-500" style={{ width: `${completionPercent}%` }} />
          </div>
          <p className="mt-3 text-lg font-bold">{completionPercent}% complete</p>
        </div>

        <nav className="pb-8">
          {course.chapters.map((chapter) => (
            <ChapterNav
              key={chapter.id}
              chapter={chapter}
              expanded={expandedChapterIds.has(chapter.id)}
              activeLessonId={activeLesson?.id}
              completedSet={completedSet}
              onToggle={() => toggleChapter(chapter.id)}
              onSelectLesson={selectLesson}
            />
          ))}
        </nav>
      </aside>

      <main className="min-w-0 overflow-y-auto p-4 lg:col-span-2">
        {activeLesson ? (
          <>
            <header className="flex min-h-10 items-center">
              <h2 className="m-0 text-xl font-bold leading-snug">{activeLesson.title}</h2>
            </header>
            <div className="mt-2 flex min-h-96 items-center justify-center bg-black">
              <VideoPlayer
                key={activeLesson.id}
                video={activeLesson.video}
                title={activeLesson.title}
                onCompleted={() => completeLesson(activeLesson)}
              />
            </div>
            <div className="mt-4 flex items-center justify-between gap-3">
              <LessonNavButton
                lesson={previousLesson}
                direction="previous"
                onSelect={selectLesson}
              />
              <LessonNavButton lesson={nextLesson} direction="next" onSelect={selectLesson} />
            </div>
          </>
        ) : (
          <div className="grid min-h-screen place-items-center text-gray-300">
            No lessons have been added yet.
          </div>
        )}
      </main>
    </div>
  )
}

function LessonNavButton({
  lesson,
  direction,
  onSelect
}: {
  lesson?: CourseificLesson
  direction: 'previous' | 'next'
  onSelect: (lesson: CourseificLesson) => void
}) {
  const label = direction == 'previous' ? 'Previous' : 'Next'

  return (
    <button
      type="button"
      disabled={!lesson}
      aria-label={lesson ? `${label}: ${lesson.title}` : label}
      className="inline-flex min-h-10 max-w-xs items-center gap-2 rounded border border-gray-600 bg-gray-800 px-3 py-2 text-base font-bold text-white enabled:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
      onClick={() => {
        if (lesson) onSelect(lesson)
      }}
    >
      {direction == 'previous' ? <ArrowLeftIcon className="size-4" /> : null}
      <span>{label}</span>
      {direction == 'next' ? <ArrowRightIcon className="size-4" /> : null}
    </button>
  )
}

function ChapterNav({
  chapter,
  expanded,
  activeLessonId,
  completedSet,
  onToggle,
  onSelectLesson
}: {
  chapter: CourseificChapter
  expanded: boolean
  activeLessonId?: string
  completedSet: Set<string>
  onToggle: () => void
  onSelectLesson: (lesson: CourseificLesson) => void
}) {
  const completedCount = chapter.lessons.filter((lesson) => completedSet.has(lesson.id)).length

  return (
    <section className="border-b border-gray-700">
      <button
        type="button"
        className="flex min-h-14 w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-4 text-left font-sans text-white hover:bg-gray-700"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="size-5 shrink-0 rounded-full border-2 border-gray-500" />
        <span className="min-w-0 flex-1 text-lg font-bold">{chapter.title}</span>
        <span className="shrink-0 text-lg font-bold">
          {completedCount} / {chapter.lessons.length}
        </span>
        <ChevronDownIcon
          className={
            expanded ? 'size-5 shrink-0 text-gray-300' : 'size-5 shrink-0 -rotate-90 text-gray-300'
          }
        />
      </button>

      {expanded ? (
        <ol className="m-0 list-none p-0">
          {chapter.lessons.map((lesson) => (
            <li key={lesson.id}>
              <button
                type="button"
                className={
                  lesson.id == activeLessonId
                    ? 'flex w-full cursor-pointer gap-2 border-0 border-l-4 border-blue-500 bg-gray-700 px-4 py-3 text-left font-sans text-white'
                    : 'flex w-full cursor-pointer gap-2 border-0 border-l-4 border-transparent bg-transparent px-4 py-3 text-left font-sans text-white hover:bg-gray-700'
                }
                onClick={() => onSelectLesson(lesson)}
              >
                <LessonStatusIcon
                  active={lesson.id == activeLessonId}
                  completed={completedSet.has(lesson.id)}
                />
                <span className="min-w-0">
                  <span className="block text-lg font-bold leading-snug">{lesson.title}</span>
                  <span className="mt-2 block text-sm font-bold uppercase text-gray-300">
                    Video - {lesson.durationMinutes} min
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  )
}

function LessonStatusIcon({ active, completed }: { active: boolean; completed: boolean }) {
  if (completed) {
    return (
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-blue-500 bg-blue-500 text-white">
        <CheckIcon className="size-3.5" />
      </span>
    )
  }

  return (
    <span
      className={
        active
          ? 'size-5 shrink-0 rounded-full border-2 border-blue-500'
          : 'size-5 shrink-0 rounded-full border-2 border-gray-500'
      }
    />
  )
}

function VideoPlayer({
  video,
  title,
  onCompleted
}: {
  video: CourseificVideo
  title: string
  onCompleted: () => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const onCompletedRef = useRef(onCompleted)

  useEffect(() => {
    onCompletedRef.current = onCompleted
  }, [onCompleted])

  useEffect(() => {
    return mountBunnyPlayer(containerRef.current!, video, title, () => onCompletedRef.current())
  }, [video.libraryId, video.videoId, title])

  return <div ref={containerRef} className="aspect-video w-full" />
}

function ArrowLeftIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
      <path
        fillRule="evenodd"
        d="M17 10a.75.75 0 0 1-.75.75H5.56l4.22 4.22a.75.75 0 1 1-1.06 1.06l-5.5-5.5a.75.75 0 0 1 0-1.06l5.5-5.5a.75.75 0 0 1 1.06 1.06L5.56 9.25h10.69A.75.75 0 0 1 17 10Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function ArrowRightIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
      <path
        fillRule="evenodd"
        d="M3 10a.75.75 0 0 1 .75-.75h10.69l-4.22-4.22a.75.75 0 1 1 1.06-1.06l5.5 5.5a.75.75 0 0 1 0 1.06l-5.5 5.5a.75.75 0 1 1-1.06-1.06l4.22-4.22H3.75A.75.75 0 0 1 3 10Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function CheckIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
      <path
        fillRule="evenodd"
        d="M16.7 4.15a.75.75 0 0 1 .15 1.05l-8 10.5a.75.75 0 0 1-1.13.08l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.9 3.89 7.47-9.81a.75.75 0 0 1 1.05-.15Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function ChevronDownIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
      <path
        fillRule="evenodd"
        d="M5.22 7.22a.75.75 0 0 1 1.06 0L10 10.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 8.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  )
}
